import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';
import BookingPage from './BookingPage';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  min-height: 100vh;
  background: #f8f9fa;
`;

const HeaderSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  text-align: center;
`;

const PropertyTitle = styled.h1`
  margin: 0 0 10px 0;
  color: #333;
  font-size: 32px;
  font-weight: 700;
`;

const PropertySubtitle = styled.p`
  margin: 0 0 20px 0;
  color: #666;
  font-size: 18px;
`;

const ShareStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin: 20px 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const StatItem = styled.div`
  text-align: center;
  
  .number {
    font-size: 24px;
    font-weight: 700;
    color: #007AFF;
    margin-bottom: 4px;
  }
  
  .label {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const ShareSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const ShareTitle = styled.h2`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
`;

const ShareButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 30px;
`;

const ShareButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.facebook {
    background: #1877f2;
    color: white;
    
    &:hover {
      background: #166fe5;
    }
  }
  
  &.twitter {
    background: #1da1f2;
    color: white;
    
    &:hover {
      background: #1a91da;
    }
  }
  
  &.linkedin {
    background: #0077b5;
    color: white;
    
    &:hover {
      background: #006ba1;
    }
  }
  
  &.copy {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
    }
  }
  
  &.email {
    background: #6f42c1;
    color: white;
    
    &:hover {
      background: #5a359a;
    }
  }
`;

const ShareUrl = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  font-family: monospace;
  font-size: 14px;
  color: #495057;
  word-break: break-all;
  text-align: center;
  margin-bottom: 20px;
`;

const BookingSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #dc3545;
  
  h2 {
    margin: 0 0 15px 0;
    color: #dc3545;
  }
  
  p {
    margin: 0;
    color: #666;
  }
`;

const SharedPropertyPage = () => {
  const { propertySlug } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareStats, setShareStats] = useState({
    views: 0,
    shares: 0,
    bookings: 0
  });

  useEffect(() => {
    loadPropertyData();
    // Track view
    trackPropertyView();
  }, [propertySlug]);

  const loadPropertyData = async () => {
    try {
      setLoading(true);
      // This would be a new API endpoint for public property viewing
      const propertyData = await showingbookerAPI.getSharedProperty(propertySlug);
      setProperty(propertyData);
      setShareStats({
        views: propertyData.view_count || 0,
        shares: propertyData.share_count || 0,
        bookings: propertyData.booking_count || 0
      });
    } catch (error) {
      console.error('Error loading shared property:', error);
      setError('Property not found or no longer available');
    } finally {
      setLoading(false);
    }
  };

  const trackPropertyView = async () => {
    try {
      // Track that someone viewed this shared property
      await showingbookerAPI.trackPropertyView(propertySlug);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  };

  const trackShare = async (platform) => {
    try {
      await showingbookerAPI.trackPropertyShare(propertySlug, platform);
      setShareStats(prev => ({ ...prev, shares: prev.shares + 1 }));
    } catch (error) {
      console.error('Error tracking share:', error);
    }
  };

  const shareUrl = `${window.location.origin}/property/share/${propertySlug}`;
  
  const shareTexts = {
    facebook: `Check out this property! Perfect for showings.`,
    twitter: `🏠 Found an amazing property! Book a showing instantly: `,
    linkedin: `Sharing this property opportunity with my network. Easy booking available.`,
    email: `I wanted to share this property with you. You can book a showing directly through the link.`
  };

  const handleSocialShare = (platform) => {
    const text = shareTexts[platform];
    let url = '';
    
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
    trackShare(platform);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      trackShare('copy');
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      trackShare('copy');
    }
  };

  const handleEmailShare = () => {
    const subject = `Property Showing Opportunity - ${property?.address_line1}`;
    const body = `Hi there,\n\n${shareTexts.email}\n\nProperty: ${property?.address_line1}, ${property?.city}, ${property?.state}\n\nBooking Link: ${shareUrl}\n\nBest regards`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(emailUrl);
    trackShare('email');
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>Loading property details...</LoadingState>
      </PageContainer>
    );
  }

  if (error || !property) {
    return (
      <PageContainer>
        <ErrorState>
          <h2>Property Not Found</h2>
          <p>{error || 'This property may have been removed or is no longer available.'}</p>
          <button 
            onClick={() => navigate('/')}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: '#007AFF',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Go to ShowingBooker Home
          </button>
        </ErrorState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Property Header */}
      <HeaderSection>
        <PropertyTitle>
          {property.address_line1}
          {property.unit_number && `, ${property.unit_number}`}
        </PropertyTitle>
        <PropertySubtitle>
          {property.city}, {property.state} {property.zip_code}
        </PropertySubtitle>
        
        <ShareStats>
          <StatItem>
            <div className="number">{shareStats.views}</div>
            <div className="label">Views</div>
          </StatItem>
          <StatItem>
            <div className="number">{shareStats.shares}</div>
            <div className="label">Shares</div>
          </StatItem>
          <StatItem>
            <div className="number">{shareStats.bookings}</div>
            <div className="label">Bookings</div>
          </StatItem>
        </ShareStats>
      </HeaderSection>

      {/* Share Section */}
      <ShareSection>
        <ShareTitle>📤 Share This Property</ShareTitle>
        
        <ShareUrl>{shareUrl}</ShareUrl>
        
        <ShareButtons>
          <ShareButton className="copy" onClick={handleCopyLink}>
            📋 {linkCopied ? 'Copied!' : 'Copy Link'}
          </ShareButton>
          <ShareButton className="facebook" onClick={() => handleSocialShare('facebook')}>
            📘 Facebook
          </ShareButton>
          <ShareButton className="twitter" onClick={() => handleSocialShare('twitter')}>
            🐦 Twitter
          </ShareButton>
          <ShareButton className="linkedin" onClick={() => handleSocialShare('linkedin')}>
            💼 LinkedIn
          </ShareButton>
          <ShareButton className="email" onClick={handleEmailShare}>
            ✉️ Email
          </ShareButton>
        </ShareButtons>
      </ShareSection>

      {/* Embedded Booking */}
      <BookingSection>
        <h2 style={{ margin: '0 0 20px 0', textAlign: 'center', color: '#333' }}>
          📅 Book a Showing
        </h2>
        <BookingPage embedded={true} propertySlug={propertySlug} parentProperty={property} />
      </BookingSection>
    </PageContainer>
  );
};

export default SharedPropertyPage;